import { useEffect, useState } from 'react';
import ProcurementNav from '../components/ProcurementNav';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api, { inventoryApi } from '../services/api';
import {
  Truck,
  Package,
  AlertTriangle,
  Check,
  X,
  ClipboardCheck,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import HelpButton from '../components/HelpButton';
import clsx from 'clsx';

interface POItem {
  id: string;
  inventoryItemId: string;
  quantity: number;
  unitCostCents: number;
  receivedQuantity?: number;
  damagedQuantity?: number;
  inventoryItem?: {
    id: string;
    name: string;
    unitOfMeasure?: string;
  };
}

interface PurchaseOrder {
  id: string;
  poNumber: string;
  status: string;
  totalCents: number;
  expectedDelivery?: string;
  createdAt: string;
  notes?: string;
  vendor?: { name: string };
  items: POItem[];
}

interface ReceiveLine {
  poItemId: string;
  receivedQuantity: number;
  damagedQuantity: number;
  note: string;
}

interface ReceiveSummaryItem {
  poItemId: string;
  itemName: string;
  ordered: number;
  received: number;
  damaged: number;
  variance: number;
}

interface ReceiveSummary {
  status: string;
  fullyReceived: boolean;
  items: ReceiveSummaryItem[];
}

const formatCurrency = (cents: number) => `$${((Number(cents) || 0) / 100).toFixed(2)}`;

const formatDate = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const itemLabel = (item: POItem) => item.inventoryItem?.name || `Item ${item.inventoryItemId.slice(0, 8)}`;
const itemUnit = (item: POItem) => item.inventoryItem?.unitOfMeasure || 'units';

function DeliveryPhotos({ poId }: { poId: string }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [thumbs, setThumbs] = useState<Record<number, string>>({});
  const { data } = useQuery({
    queryKey: ['receiving-photos', poId],
    queryFn: async () => (await api.get(`/v1/inventory/purchase-orders/${poId}/receiving-photos`)).data?.data || { photos: [] },
  });
  const photos: Array<{ index: number; at: string }> = data?.photos || [];
  useEffect(() => {
    let dead = false;
    (async () => {
      for (const ph of photos) {
        if (thumbs[ph.index]) continue;
        try {
          const res = await api.get(`/v1/inventory/purchase-orders/${poId}/receiving-photos/${ph.index}`, { responseType: 'blob' });
          if (!dead) setThumbs((t) => ({ ...t, [ph.index]: URL.createObjectURL(res.data) }));
        } catch { /* thumb stays empty */ }
      }
    })();
    return () => { dead = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos.length, poId]);
  const upload = async (file: File) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/v1/inventory/purchase-orders/${poId}/receiving-photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      qc.invalidateQueries({ queryKey: ['receiving-photos', poId] });
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 pt-3 border-t border-slate-100">
      <div className="flex items-center gap-2 flex-wrap">
        {photos.map((ph) => (
          thumbs[ph.index]
            ? <a key={ph.index} href={thumbs[ph.index]} target="_blank" rel="noreferrer" title={new Date(ph.at).toLocaleString('en-US')}>
                <img src={thumbs[ph.index]} className="w-12 h-12 rounded-lg object-cover border border-slate-200" />
              </a>
            : <div key={ph.index} className="w-12 h-12 rounded-lg bg-slate-100 animate-pulse" />
        ))}
        <label className={`text-xs px-2.5 py-1.5 rounded-lg font-medium cursor-pointer ${busy ? 'bg-slate-100 text-slate-400' : 'bg-slate-50 border border-slate-200 text-slate-600 hover:bg-slate-100'}`}>
          {busy ? 'Saving…' : photos.length ? '📷 Add photo' : '📷 Photos of boxes & docs'}
          <input type="file" accept="image/*,.heic,.heif" className="hidden" disabled={busy}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ''; }} />
        </label>
        {photos.length === 0 && <span className="text-[11px] text-slate-400">snap the pallet and the packing slip as the truck leaves — they stay on this delivery</span>}
      </div>
    </div>
  );
}

export default function ReceivingPage() {
  const queryClient = useQueryClient();
  const [activePO, setActivePO] = useState<PurchaseOrder | null>(null);
  const [lines, setLines] = useState<Record<string, ReceiveLine>>({});
  const [deliveryNotes, setDeliveryNotes] = useState('');
  const [deliveryInvoiceNumber, setDeliveryInvoiceNumber] = useState('');
  const [formError, setFormError] = useState('');
  const [summary, setSummary] = useState<ReceiveSummary | null>(null);
  const [showReceived, setShowReceived] = useState(false);

  // Fetch purchase orders
  const { data: poData, isLoading } = useQuery({
    queryKey: ['purchase-orders'],
    queryFn: async () => {
      const response = await inventoryApi.getPurchaseOrders({});
      const result = response.data?.data || response.data;
      return Array.isArray(result) ? (result as PurchaseOrder[]) : [];
    },
  });

  const orders = Array.isArray(poData) ? poData : [];
  // drafts aren't trucks — they live in Ordering until they're sent
  const pendingOrders = orders.filter((po) => !['received', 'draft', 'cancelled'].includes(po.status));
  const receivedOrders = orders.filter((po) => po.status === 'received');

  const receiveMutation = useMutation({
    mutationFn: ({ orderId, data }: { orderId: string; data: { items: ReceiveLine[]; deliveryNotes?: string } }) =>
      inventoryApi.receivePurchaseOrder(orderId, data),
    onSuccess: (response: any) => {
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      const result: ReceiveSummary | null = response?.data?.data || response?.data || null;
      const hasVariance = !!result?.items?.some((i) => i.variance !== 0 || i.damaged > 0);
      if (result && hasVariance) {
        setSummary(result);
      } else {
        setSummary(null);
      }
      closePanel();
    },
    onError: (error: any) => {
      setFormError(error.response?.data?.error?.message || 'Failed to receive order');
    },
  });

  const openPanel = (po: PurchaseOrder) => {
    const initial: Record<string, ReceiveLine> = {};
    po.items.forEach((item) => {
      initial[item.id] = {
        poItemId: item.id,
        receivedQuantity: Number(item.quantity) || 0,
        damagedQuantity: 0,
        note: '',
      };
    });
    setLines(initial);
    setDeliveryNotes('');
    setFormError('');
    setSummary(null);
    setActivePO(po);
  };

  const closePanel = () => {
    setActivePO(null);
    setLines({});
    setDeliveryNotes('');
    setFormError('');
  };

  const updateLine = (poItemId: string, patch: Partial<ReceiveLine>) => {
    setLines((prev) => ({
      ...prev,
      [poItemId]: { ...prev[poItemId], ...patch },
    }));
  };

  const receiveInFull = () => {
    if (!activePO) return;
    const next: Record<string, ReceiveLine> = {};
    activePO.items.forEach((item) => {
      next[item.id] = {
        poItemId: item.id,
        receivedQuantity: Number(item.quantity) || 0,
        damagedQuantity: 0,
        note: lines[item.id]?.note || '',
      };
    });
    setLines(next);
  };

  const handleConfirm = () => {
    if (!activePO) return;
    setFormError('');
    const items: ReceiveLine[] = activePO.items.map((item) => {
      const line = lines[item.id];
      return {
        poItemId: item.id,
        receivedQuantity: Number(line?.receivedQuantity) || 0,
        damagedQuantity: Number(line?.damagedQuantity) || 0,
        note: line?.note || '',
      };
    });
    receiveMutation.mutate({
      orderId: activePO.id,
      data: { items, deliveryNotes: [deliveryInvoiceNumber.trim() ? `Invoice #${deliveryInvoiceNumber.trim()}` : null, deliveryNotes || null].filter(Boolean).join(' · ') || undefined },
    });
  };

  const statusBadge = (status: string) => {
    const map: Record<string, string> = {
      draft: 'badge-warning',
      partial: 'badge-warning',
      received: 'badge-success',
    };
    return (
      <span className={clsx('badge text-xs', map[status] || 'badge-warning')}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const varianceColor = (variance: number, damaged: number) => {
    if (variance < 0) return 'text-danger-600';
    if (variance > 0 || damaged > 0) return 'text-amber-600';
    return 'text-emerald-600';
  };

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6 pb-safe">
      <ProcurementNav />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-slate-900">Receiving</h1>
            <p className="text-sm md:text-base text-slate-500">
              Match deliveries to orders and flag shortages or damage.
            </p>
          </div>
          <HelpButton featureId="receiving" />
        </div>
      </div>

      {/* Success summary with variances */}
      {summary && (
        <div className="card p-4 bg-warning-50 border-warning-200">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-warning-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <div className="flex items-center justify-between gap-2">
                <p className="font-medium text-warning-800">
                  Delivery received with variances {summary.fullyReceived ? '' : '(partial)'}
                </p>
                <button
                  onClick={() => setSummary(null)}
                  className="p-1 hover:bg-warning-100 rounded-lg"
                >
                  <X className="w-4 h-4 text-warning-600" />
                </button>
              </div>
              <div className="mt-2 space-y-1 text-sm">
                {summary.items
                  .filter((i) => i.variance !== 0 || i.damaged > 0)
                  .map((i) => (
                    <div key={i.poItemId} className="text-warning-700">
                      <span className="font-medium">{i.itemName}:</span> ordered {i.ordered}, received {i.received}
                      {i.damaged > 0 && `, ${i.damaged} damaged`}
                      <span className={clsx('ml-1 font-medium', varianceColor(i.variance, i.damaged))}>
                        ({i.variance > 0 ? '+' : ''}{i.variance})
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-12 text-slate-500">Loading...</div>
      ) : (
        <>
          {/* Pending Deliveries */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-slate-700" />
              <h2 className="text-lg font-semibold text-slate-900">Pending Deliveries</h2>
              <span className="text-sm text-slate-500">({pendingOrders.length})</span>
            </div>

            {pendingOrders.length === 0 ? (
              <div className="card text-center py-12 text-slate-500">
                <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No deliveries awaiting receipt</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {pendingOrders.map((po) => (
                  <div key={po.id} className="card p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-semibold text-slate-900 truncate">{po.poNumber}</p>
                          {statusBadge(po.status)}
                        </div>
                        <p className="text-sm text-slate-500 truncate">{po.vendor?.name || 'Unknown vendor'}</p>
                      </div>
                      <button
                        onClick={() => openPanel(po)}
                        className="btn btn-primary btn-sm flex items-center gap-1.5 flex-shrink-0"
                      >
                        <ClipboardCheck className="w-4 h-4" />
                        Receive
                      </button>
                    </div>
                    <DeliveryPhotos poId={po.id} />
                    <div className="grid grid-cols-3 gap-2 mt-3 text-sm">
                      <div>
                        <p className="text-slate-400 text-xs">Expected</p>
                        <p className="font-medium text-slate-700">{formatDate(po.expectedDelivery)}</p>
                      </div>
                      <div>
                        <p className="text-slate-400 text-xs">Items</p>
                        <p className="font-medium text-slate-700">{po.items?.length || 0}</p>
                      </div>
                      <div>
                        <p className="text-slate-400 text-xs">Total</p>
                        <p className="font-medium text-slate-700">{formatCurrency(po.totalCents)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recently Received (collapsed/secondary) */}
          {receivedOrders.length > 0 && (
            <div className="card overflow-hidden">
              <button
                onClick={() => setShowReceived(!showReceived)}
                className="w-full p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-emerald-500" />
                  <span className="font-medium text-slate-800">Recently Received</span>
                  <span className="text-sm text-slate-500">({receivedOrders.length})</span>
                </div>
                {showReceived ? (
                  <ChevronUp className="w-5 h-5 text-slate-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-slate-400" />
                )}
              </button>
              {showReceived && (
                <div className="divide-y border-t">
                  {receivedOrders.map((po) => (
                    <div key={po.id} className="px-4 py-3 flex items-center justify-between text-sm">
                      <div className="min-w-0">
                        <p className="font-medium text-slate-800 truncate">{po.poNumber}</p>
                        <p className="text-slate-500 truncate">{po.vendor?.name || 'Unknown vendor'}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="font-medium text-slate-700">{formatCurrency(po.totalCents)}</p>
                        <p className="text-slate-400 text-xs">{po.items?.length || 0} items</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Receiving Panel / Modal */}
      {activePO && (
        <div className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center z-50">
          <div className="bg-white rounded-t-2xl md:rounded-xl shadow-xl w-full md:max-w-2xl max-h-[92vh] md:max-h-[88vh] overflow-y-auto pb-safe">
            {/* Panel header */}
            <div className="flex items-center justify-between p-5 md:p-6 border-b sticky top-0 bg-white z-10">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-bold truncate">{activePO.poNumber}</h2>
                  {statusBadge(activePO.status)}
                </div>
                <p className="text-sm text-slate-500 truncate">
                  {activePO.vendor?.name || 'Unknown vendor'} · {activePO.items?.length || 0} items
                </p>
              </div>
              <button onClick={closePanel} className="p-2 hover:bg-slate-100 rounded-lg flex-shrink-0">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 md:p-6 space-y-4">
              {formError && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                  {formError}
                </div>
              )}

              <div className="flex justify-end">
                <button
                  onClick={receiveInFull}
                  className="btn btn-secondary btn-sm flex items-center gap-1.5"
                >
                  <Check className="w-4 h-4" />
                  Receive in full
                </button>
              </div>

              {/* Lines */}
              <div className="space-y-3">
                {activePO.items.map((item) => {
                  const line = lines[item.id];
                  const ordered = Number(item.quantity) || 0;
                  const received = Number(line?.receivedQuantity) || 0;
                  const damaged = Number(line?.damagedQuantity) || 0;
                  const variance = received - ordered;
                  return (
                    <div key={item.id} className="border border-slate-200 rounded-lg p-3">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <p className="font-medium text-slate-900 truncate">{itemLabel(item)}</p>
                        <span className="text-sm text-slate-500 flex-shrink-0">
                          Ordered: {ordered} {itemUnit(item)}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Received</label>
                          <input
                            type="number"
                            min={0}
                            step="any"
                            className="input w-full"
                            value={received}
                            onChange={(e) =>
                              updateLine(item.id, { receivedQuantity: parseFloat(e.target.value) || 0 })
                            }
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Damaged</label>
                          <input
                            type="number"
                            min={0}
                            step="any"
                            className="input w-full"
                            value={damaged}
                            onChange={(e) =>
                              updateLine(item.id, { damagedQuantity: parseFloat(e.target.value) || 0 })
                            }
                          />
                        </div>
                      </div>
                      <input
                        type="text"
                        placeholder="Note (optional)"
                        className="input w-full mt-2 text-sm"
                        value={line?.note || ''}
                        onChange={(e) => updateLine(item.id, { note: e.target.value })}
                      />
                      <div className="mt-2 text-sm flex items-center gap-1">
                        <span className="text-slate-400">Variance:</span>
                        <span className={clsx('font-medium', varianceColor(variance, damaged))}>
                          {variance > 0 ? '+' : ''}{variance} {itemUnit(item)}
                          {variance < 0 && ' (short)'}
                          {variance > 0 && ' (over)'}
                          {damaged > 0 && ` · ${damaged} damaged`}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Invoice number — ties this delivery to the bill for the 3-way match */}
              <div className="mb-3">
                <label className="block text-sm font-medium text-slate-700 mb-1">Invoice number on the slip</label>
                <input
                  className="input w-full max-w-xs"
                  placeholder="e.g. 9621993"
                  value={deliveryInvoiceNumber}
                  onChange={(e) => setDeliveryInvoiceNumber(e.target.value)}
                />
                <p className="text-xs text-slate-400 mt-1">Damages and shortages you enter per line become the reconciliation against this invoice.</p>
              </div>
              {/* Delivery notes */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Delivery notes</label>
                <textarea
                  className="input w-full"
                  rows={2}
                  placeholder="e.g. Driver late, packaging issues, substitutions..."
                  value={deliveryNotes}
                  onChange={(e) => setDeliveryNotes(e.target.value)}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 p-5 md:p-6 border-t sticky bottom-0 bg-white">
              <button onClick={closePanel} className="btn btn-secondary">
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={receiveMutation.isPending}
                className="btn btn-primary flex items-center gap-1.5"
              >
                <ClipboardCheck className="w-4 h-4" />
                {receiveMutation.isPending ? 'Receiving...' : 'Confirm Receipt'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
